import type { IPickPageItem, IPickPageSource } from '../../types';

import { useMemo } from 'react';

// Two page types can carry the same platform id, so a merged row is re-keyed by the type behind it:
// the list keys rows by `id` and the picker tracks its selection by the same field.
const toMergedItem = (item: IPickPageItem): IPickPageItem => ({
  ...item,
  id: `${item.type}:${item.id}`,
});

/**
 * Reads every page-type source as one list, for a search that spans all of them.
 *
 * Rows stay grouped in the order the mode lists its types, each group latest-update first: the API
 * reports no comparable "updated at" across types, so a merged list has nothing to sort itself by.
 */
export const usePickPageMergedSource = (sources: IPickPageSource[]): IPickPageSource => {
  const items = useMemo(
    () => sources.flatMap((source) => source.items.map(toMergedItem)),
    // One dep per source, so the merged list rebuilds only when a source's own list does. The mode's
    // type list is fixed, so this dep array keeps its length across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    sources.map((source) => source.items),
  );

  const isFetchingNextPage = sources.some((source) => source.isFetchingNextPage);

  // Pages the first group that still has more, so the groups already on show keep their order.
  const handleFetchNextPage = () => {
    if (isFetchingNextPage) return;

    sources.find((source) => source.hasNextPage)?.handleFetchNextPage();
  };

  const handleRefresh = () => Promise.all(sources.map((source) => source.handleRefresh()));

  return {
    items,
    isLoading: sources.some((source) => source.isLoading),
    isError: sources.some((source) => source.isError),
    // Only a search reaches this source, so an empty list is always a search that matched nothing —
    // never a store holding no pages at all.
    isSourceEmpty: false,
    isFetchingNextPage,
    hasNextPage: sources.some((source) => source.hasNextPage),
    handleFetchNextPage,
    handleRefresh,
  };
};
